import { IonSpinner, IonThumbnail } from '@ionic/react';
import { isPlatform } from '../../../utils/isPlatform';
import { ReactNode, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { DuoClapperboardPlayIcon, DuoDocFileIcon, DuoFileIcon, DuoPDFFileIcon } from '../../../theme/Icons';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { useResolvedMediaUrl } from '../../../media/useResolvedMediaUrl';
import { AuthorizedMediaUrlResolver } from '../../../media/AuthorizedMediaUrlResolver';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Href } from '../../../components/Href';
import { Logger } from '../../../support/Logger';
import { useToast } from '../../../components/useToast';
import { IAttachmentLink } from './IAttachment';

const useStyles = createUseStyles({
    thumbnail: {
        width: 110,
        '--size': '70px',
        height: 70,
        '--border-radius': '5px',
        borderRadius: 5,
        border: '1px solid var(--ion-color-step-200)',
        backgroundColor: 'var(--ion-color-light)',
        //overflow: 'hidden',
        position: 'relative'
    },

    document: {
        padding: 6
    },

    iconWrapper: {
        marginRight: 4,
        display: 'inline-block'
    },

    thumbnailMask: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'white',
        opacity: 0.8,

        '& ion-spinner': {
            width: '70%',
            height: '90%'
        }
    },

    innerWrapper: {
        fontSize: 14,
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 3,
        // for non-webkit browsers (Firefox)
        minHeight: '100%',
        maxHeight: '3.8rem',
        lineHeight: 'initial'
    },

    videoIconWrapper: {
        display: 'flex',
        height: '100%',

        '& > ion-text': {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    }
});

type INoteAttachmentProps = {
    url: string;
    fileName: string;
    isUploading?: boolean;
    contentType?: string;
    // Present when the API offered the authorised download route for this attachment.
    links?: { download?: IAttachmentLink };
};


type IAttachmentButton = INoteAttachmentProps & {
    children: ReactNode;
};

const attachmentsDirPath = 'attachments';
const NoteAttachmentButton: React.FC<IAttachmentButton> = ({ url, fileName, children, isUploading, links }) => {
    const classes = useStyles();
    const [downloading, setDownloading] = useState<boolean>(false);
    const resolver = useInstance(AuthorizedMediaUrlResolver);
    const log = useInstance(Logger);
    const [showToast] = useToast();

    // Resolved when the attachment is opened rather than when it is rendered. The credential in an
    // authorised url lasts minutes, and a note can sit open for far longer than that, so a url
    // baked in at render is one the server would refuse by the time anyone clicked it.
    //
    // Returns undefined rather than throwing when it cannot be authorised. The resolver refuses a
    // read it cannot account for, and Href does not await or catch the handler it is given, so a
    // throw here becomes an unhandled rejection: the tap does nothing, the caregiver is told
    // nothing, and nobody finds out. Reported to the caregiver AND to Sentry instead, and every
    // caller must check for undefined before using it.
    const resolveNow = async (): Promise<string | undefined> => {
        try {
            return await resolver.resolve({ url, href: links?.download?.href });
        } catch (e) {
            log.error(e, { context: 'could not authorise an attachment read', fileName });
            await showToast({ duration: 3000, message: 'Sorry, that attachment could not be opened' });
            return undefined;
        }
    };

    return !isUploading ? isPlatform('capacitor') ? (
        // native device
        downloading ? (
            <>
                {children}
                <div className={classes.thumbnailMask}>
                    <IonSpinner name="dots" color="primary" />
                </div>
            </>
        ) : (
            <Href
                onClick={async () => {
                    const resolvedUrl = await resolveNow();
                    if (!resolvedUrl) {
                        // resolveNow has already told the caregiver and Sentry. Opening the file
                        // is impossible without an authorised url, and falling back to the stored
                        // one would be an unaudited read of the public bucket.
                        return;
                    }

                    try {
                        // create unique file path (directory) for each file
                        let localFilePath: string | undefined;
                        // Keyed on the stable bucket url, NOT the resolved one: that carries a
                        // session token, so it would change every session (defeating the cache)
                        // and write a live credential into a path on disk.
                        const base64S3Path = btoa(encodeURIComponent(url));
                        const filePath = `${attachmentsDirPath}/${base64S3Path}/${fileName.replace(/\//g, '-')}`;

                        try {
                            // has the file already been downloaded?
                            const { uri } = await Filesystem.stat({
                                path: filePath,
                                directory: Directory.Cache
                            });

                            localFilePath = uri;
                        } catch (err: any) {
                            // file hasn't been downloaded, download it and show an indicator in case slow
                            setDownloading(true);

                            // android needs us to create the directories first
                            if (isPlatform('android')) {
                                await Filesystem.mkdir({
                                    recursive: true,
                                    directory: Directory.Cache,
                                    path: `${attachmentsDirPath}/${base64S3Path}`
                                });
                            }

                            const { path } = await Filesystem.downloadFile({
                                url: resolvedUrl,
                                path: filePath,
                                directory: Directory.Cache
                            });

                            setDownloading(false);
                            if (path) {
                                localFilePath = path;
                            } else {
                                throw new Error(`Filesystem.downloadFile failed? ${filePath} was not downloaded successfully`);
                            }
                        }

                        // open it in the viewer
                        await FileOpener.open({
                            openWithDefault: true,
                            filePath: localFilePath
                        });
                    } catch (err: any) {
                        // '8' FILE_NOT_SUPPORTED
                        // https://github.com/capacitor-community/file-opener?tab=readme-ov-file#api
                        if (err.code === '8') {
                            const link = document.createElement('a');
                            link.href = resolvedUrl;
                            link.target = '_blank';
                            link.click();
                        }

                        // log to Sentry so we can see if we need to deal with any specific errors
                        throw err;
                    }
                }}
            >
                {children}
            </Href>
        )
    ) : (
        // browser
        // PDF / Image - on web, opens in another tab, no issue
        // Image - on android, opens in separate chrome app, no issue
        // PDF - on android, opens  in separate chrome app, downloads, then I have to open it, finally picking what to open it with
        //    by then, back is hosed (maybe)
        <Href
            onClick={async () => {
                // The tab is opened before awaiting so the browser still attributes it to the
                // click; one opened after the await is treated as an unsolicited popup and blocked.
                const tab = window.open('', '_blank');

                // Severed by hand rather than by window.open's 'noopener' feature, which would
                // return null and leave nothing to navigate once the url resolves. Without this the
                // attachment — whose content type we do not control — keeps a live handle on the
                // app window and could redirect it out from under the caregiver.
                if (tab) {
                    tab.opener = null;
                }

                const resolvedUrl = await resolveNow();
                if (!resolvedUrl) {
                    // Closed rather than left sitting: a blank tab the caregiver has to notice and
                    // dismiss reads as the app hanging. resolveNow has already reported the failure.
                    tab?.close();
                    return;
                }

                if (tab) {
                    tab.location.href = resolvedUrl;
                }
            }}
        >
            {children}
        </Href>

    ) : children;
};

export const NoteAttachment: React.FC<INoteAttachmentProps> = (props) => {
    const classes = useStyles();

    const {fileName, isUploading = false, contentType} = props;
    const resolvedUrl = useResolvedMediaUrl({ url: props.url, href: props.links?.download?.href });

    const getCompForContentType = () => {
        const mimeType = contentType?.split('/')[1] || '';
        const isImage: boolean = contentType?.startsWith('image/') || false;
        const isVideo: boolean = contentType?.startsWith('video/') || false;

        const fileIcons: any = {
            'pdf': <DuoPDFFileIcon color="danger" />,
            'msword': <DuoDocFileIcon color="google-docs" />,
            'vnd.openxmlformats-officedocument.wordprocessingml.document': <DuoDocFileIcon />
        };

        return isImage && !isUploading ? (
            <IonThumbnail className={classes.thumbnail}>
                <img src={resolvedUrl} alt="attachment" className={classes.thumbnail} />
            </IonThumbnail>
        ) : isVideo && !isUploading ? (
            <IonThumbnail className={classes.thumbnail}>
                <div className={classes.videoIconWrapper}>
                    <DuoClapperboardPlayIcon customSize={42} />
                </div>
            </IonThumbnail>
        ) : (
            <div className={`${classes.thumbnail} ${classes.document}`}>
                <div className={classes.innerWrapper}>
                    <div className={classes.iconWrapper}>
                        {fileIcons[mimeType] || <DuoFileIcon />}
                    </div>
                    {fileName.substring(0, fileName.lastIndexOf('.')) || fileName}
                </div>
                {isUploading && (
                    <div className={classes.thumbnailMask}>
                        <IonSpinner name="dots" color="primary" />
                    </div>
                )}
            </div>
        );
    };

    const dataProps = {
        'data-type': isUploading ?  'uploading-file': 'attachment-button'
    };

    return (
        <div {...dataProps} style={{position: 'relative'}}>
            <NoteAttachmentButton {...props}>
                {getCompForContentType()}
            </NoteAttachmentButton>
        </div>

    );
};
